import { useAppContext } from '@faro/app-context';
import {
    CommandResponse,
    EntityCommandCreateResponse,
    RevisionedCommandResponse,
    StudyEntityType,
} from '@faro/study-designer-model';
import axios from 'axios';
import { createStudyEntityHookFactory } from './createStudyEntityHookFactory';

/** Duplicates a section of a study, resolving with the command's response. */
export type SectionDuplicateHook<R extends CommandResponse = EntityCommandCreateResponse> = (section: {
    readonly id: string;
}) => Promise<RevisionedCommandResponse<R>>;

/**
 * Creates the mutation hook that duplicates a type of section through its clone action, reporting a failure with the
 * given message.
 *
 * @param entityType the section's entity type, which keys its mutations
 * @param resourcePath the section type's path in the study API, e.g. `folder-section`
 * @param errorMessage shown when the duplicate fails
 */
export function createSectionDuplicateMutation<R extends CommandResponse = EntityCommandCreateResponse>(
    entityType: StudyEntityType,
    resourcePath: string,
    errorMessage: string
): (studyId: string) => SectionDuplicateHook<R> {
    const useDuplicate = createStudyEntityHookFactory(entityType).mutate(
        ({ id, studyId }: { id: string; studyId: string }): Promise<RevisionedCommandResponse<R>> =>
            axios
                .post(`/api/study-designer/studies/${studyId}/${resourcePath}/${id}/actions/clone`, {})
                .then(response => response.data)
    );

    return function useSectionDuplicateMutation(studyId: string): SectionDuplicateHook<R> {
        const { showErrorHandler } = useAppContext();
        const duplicate = useDuplicate({ studyId });

        return section => duplicate.mutateAsync({ id: section.id, studyId }).catch(showErrorHandler(errorMessage));
    };
}
